import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useGetHolidayAccessories from '@ubeya/shared-web/hooks/useGetHolidayAccessories';
import { ReactComponent as ExpandIcon } from '@ubeya/shared-web/assets/SidebarIcons/Modules/sidebar-expand.svg';
import {
  APP_SIDEBAR_COLLAPSED_WIDTH,
  APP_SIDEBAR_EXPANDED_WIDTH,
  APP_SIDEBAR_WIDTH_TRANSITION_MS,
  useAppSidebarExpand
} from '@ubeya/shared-web/hooks/useAppSidebarExpand';
import { FlexCenter, FlexColumn, FlexMiddle, NavigationMenu } from '@ubeya/shared-web/components';
import { media } from '@ubeya/shared-web/style';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import ConfigContext from '../../contexts/ConfigContext';
import { AppSideBarItem } from './AppSideBarItem';

const Wrapper = styled(FlexColumn).attrs(() => ({ className: 'app-side-bar-wrapper' }))`
  display: none;
  padding-top: 16px;
  padding-left: 10px;
  padding-right: 10px;
  ${media.tablet`
    position: fixed;
    top: 0;
    bottom: 0;
    inset-inline-start: 0;
    z-index: 4;
    display: flex;
  `};
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.sideBarBackground};
  transition: width ${APP_SIDEBAR_WIDTH_TRANSITION_MS}ms ease-in-out;
  width: ${({ $isExpanded }) => ($isExpanded ? `${APP_SIDEBAR_EXPANDED_WIDTH}px` : `${APP_SIDEBAR_COLLAPSED_WIDTH}px`)};
`;

const CircleIcon = styled(FlexCenter)`
  border-radius: 8px;
  cursor: pointer;
  transition: 200ms;
  width: 40px;
  min-width: 40px;
  height: 40px;
  margin-bottom: 2px;
`;

const Link = styled(FlexMiddle)`
  text-decoration: none;
  padding: ${({ $userLink }) => ($userLink ? 0 : '10px 8px')};
  width: 100%;
  transition: 300ms;
  justify-content: center;
  flex-direction: column;
  ${({ $mainIcon }) => $mainIcon && 'margin-bottom: 48px;'}
  &.active {
    > ${CircleIcon} {
      background: ${({ theme }) => theme.colors.primary};
    }
  }

  &:hover {
    ${() =>
      css`
        > ${CircleIcon} {
          background: ${({ theme }) => theme.colors.white}26;
        }
      `}
  }
`;

const Links = styled(FlexColumn)`
  width: 100%;
  gap: 10px;
`;

const BottomSection = styled(FlexColumn)`
  width: 100%;
  align-items: center;
  gap: 10px;
`;

const ToggleContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 44px;
`;

const ToggleButton = styled(CircleIcon).attrs(() => ({
  as: 'button',
  type: 'button'
}))`
  border: 0;
  padding: 0;
  background: transparent;

  &:hover {
    background: ${({ theme }) => `${theme.colors.white}26`};
  }
`;

const StyledExpandIcon = styled(ExpandIcon)`
  transition: 300ms;
  transform: ${({ $isExpanded, $isRtl }) =>
    ($isExpanded && !$isRtl) || (!$isExpanded && $isRtl) ? 'rotate(180deg)' : 'none'};
`;

const AppSideBar = ({
  links,
  linkRenderer: LinkRenderer = NavLink,
  children,
  mobileRenderer = children,
  shouldChangePageTitle = true
}) => {
  const { t, isRtl } = useContext(ConfigContext);
  const {
    account: { name, isHost }
  } = useAccount();
  const theme = useTheme();
  const [openPopoverIndex, setOpenPopoverIndex] = useState(-1);
  const { isExpanded, isAnimationFinished, setIsExpanded } = useAppSidebarExpand();
  const { pathname } = useLocation();
  const { SidebarBottomAccessory } = useGetHolidayAccessories();

  const onCollapseSidebar = useCallback(() => {
    setOpenPopoverIndex(-1);
    trackEvent('Module Sidebar Collapsed', {
      context: 'Module Sidebar',
      trigger: 'navigation'
    });
    setIsExpanded(false);
  }, [setIsExpanded]);

  const onToggleExpanded = useCallback(() => {
    if (isExpanded) {
      setOpenPopoverIndex(-1);
    }
    trackEvent(!isExpanded ? 'Module Sidebar Expanded' : 'Module Sidebar Collapsed', {
      context: 'Module Sidebar',
      trigger: 'toggle_button'
    });
    setIsExpanded((prev) => !prev);
  }, [isExpanded, setIsExpanded]);

  const flatLinks = useMemo(() => links.flat(), [links]);

  return (
    <>
      <NavigationMenu linkRenderer={LinkRenderer} links={flatLinks}>
        {React.cloneElement(mobileRenderer, { isMobile: true })}
      </NavigationMenu>

      <Wrapper $isExpanded={isExpanded}>
        <FlexColumn>
          {links.map((subLinks, index) => (
            <Links key={index}>
              {subLinks
                .filter(({ isVisible = true }) => isVisible)
                .map(
                  (
                    {
                      to,
                      icon: Icon,
                      mainIcon,
                      label,
                      mobileLabel,
                      secondaryMenu,
                      isNew,
                      includePath,
                      hasBottomBorder,
                      disabled,
                      accessory
                    },
                    linkIndex
                  ) => (
                    <AppSideBarItem
                      mainIcon={mainIcon}
                      isNew={isNew}
                      includePath={includePath}
                      isRtl={isRtl}
                      hasBottomBorder={hasBottomBorder}
                      shouldChangePageTitle={shouldChangePageTitle}
                      key={linkIndex}
                      disabled={disabled}
                      isHost={isHost}
                      index={linkIndex}
                      openPopoverIndex={openPopoverIndex}
                      onHover={(value) => setOpenPopoverIndex(value | linkIndex)}
                      to={to}
                      pathname={pathname}
                      Icon={Icon}
                      t={t}
                      theme={theme}
                      label={label}
                      mobileLabel={mobileLabel}
                      secondaryMenu={secondaryMenu}
                      LinkRenderer={LinkRenderer}
                      accountName={name}
                      accessory={accessory}
                      isExpanded={isExpanded}
                      isAnimationFinished={isAnimationFinished}
                      onCollapseSidebar={onCollapseSidebar}
                    />
                  )
                )}
            </Links>
          ))}
        </FlexColumn>

        <BottomSection>
          {!!SidebarBottomAccessory && <SidebarBottomAccessory />}
          <Link $userLink>{children}</Link>

          <ToggleContainer $isExpanded={isExpanded}>
            <ToggleButton aria-label="toggle sidebar" onClick={onToggleExpanded}>
              <StyledExpandIcon $isExpanded={isExpanded} $isRtl={isRtl} />
            </ToggleButton>
          </ToggleContainer>
        </BottomSection>
      </Wrapper>
    </>
  );
};

export default AppSideBar;
